import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Youtube, Facebook, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/Logo";
import api, { fmtErr } from "@/lib/api";

const Contact = ({ content }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Your message is on the wind!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  const socials = [
    { icon: Instagram, url: content.instagram_url, label: "Instagram", testid: "contact-social-instagram" },
    { icon: Youtube, url: content.youtube_url, label: "YouTube", testid: "contact-social-youtube" },
    { icon: WhatsAppIcon, url: content.whatsapp_url, label: "WhatsApp", testid: "contact-social-whatsapp" },
    { icon: Facebook, url: content.facebook_url, label: "Facebook", testid: "contact-social-facebook" },
  ].filter((s) => s.url);

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-paper" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="font-script text-verm text-3xl sm:text-4xl" data-testid="contact-script">
            {content.contact_script || "Say hello across the wind"}
          </p>
          <h2 className="mt-3 font-display font-bold text-navy text-4xl sm:text-5xl" data-testid="contact-heading">
            Contact Us
          </h2>

          <div className="mt-8 relative rounded-2xl overflow-hidden border-4 border-verm shadow-xl shadow-navy/20 bg-marigold/20 p-2">
            <img
              src={content.contact_image}
              alt="Folk art of families flying kites together in Odisha"
              className="w-full h-64 sm:h-80 object-cover rounded-lg"
              data-testid="contact-image"
            />
          </div>

          <ul className="mt-8 space-y-3" data-testid="contact-details">
            <li className="flex items-center gap-3 font-body font-semibold text-navy">
              <span className="w-10 h-10 rounded-full bg-navy text-marigold grid place-items-center">
                <Mail className="w-4 h-4" />
              </span>
              {content.contact_email}
            </li>
            {content.contact_phone ? (
              <li className="flex items-center gap-3 font-body font-semibold text-navy">
                <span className="w-10 h-10 rounded-full bg-navy text-marigold grid place-items-center">
                  <Phone className="w-4 h-4" />
                </span>
                {content.contact_phone}
              </li>
            ) : null}
            <li className="flex items-center gap-3 font-body font-semibold text-navy">
              <span className="w-10 h-10 rounded-full bg-navy text-marigold grid place-items-center">
                <MapPin className="w-4 h-4" />
              </span>
              {content.contact_location}
            </li>
          </ul>

          {socials.length ? (
            <div className="mt-6 flex gap-3" data-testid="contact-socials">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={s.testid}
                  className="w-11 h-11 rounded-full border-2 border-navy/25 grid place-items-center text-navy hover:bg-navy hover:text-marigold hover:border-navy transition-colors duration-300"
                >
                  <s.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          ) : null}
        </Reveal>

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            className="bg-paperdeep/70 border-2 border-navy/15 rounded-2xl p-7 sm:p-10 shadow-lg shadow-navy/10"
            data-testid="contact-form"
          >
            <div className="space-y-5">
              <div>
                <label htmlFor="cf-name" className="block font-body font-semibold text-navy text-sm mb-1.5">
                  Your Name *
                </label>
                <input
                  id="cf-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  data-testid="contact-name-input"
                  placeholder="Kiran Das"
                  className="w-full bg-paper border-2 border-navy/20 rounded-lg px-4 py-3 font-body text-navy placeholder:text-navy/40 focus:outline-none focus:border-verm transition-colors duration-300"
                />
              </div>
              <div>
                <label htmlFor="cf-email" className="block font-body font-semibold text-navy text-sm mb-1.5">
                  Email *
                </label>
                <input
                  id="cf-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  data-testid="contact-email-input"
                  placeholder="you@example.com"
                  className="w-full bg-paper border-2 border-navy/20 rounded-lg px-4 py-3 font-body text-navy placeholder:text-navy/40 focus:outline-none focus:border-verm transition-colors duration-300"
                />
              </div>
              <div>
                <label htmlFor="cf-phone" className="block font-body font-semibold text-navy text-sm mb-1.5">
                  Phone (optional)
                </label>
                <input
                  id="cf-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="contact-phone-input"
                  placeholder="+91 ..."
                  className="w-full bg-paper border-2 border-navy/20 rounded-lg px-4 py-3 font-body text-navy placeholder:text-navy/40 focus:outline-none focus:border-verm transition-colors duration-300"
                />
              </div>
              <div>
                <label htmlFor="cf-message" className="block font-body font-semibold text-navy text-sm mb-1.5">
                  Message *
                </label>
                <textarea
                  id="cf-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-message-input"
                  placeholder="Tell us about your kite story, an event, or a collaboration..."
                  className="w-full bg-paper border-2 border-navy/20 rounded-lg px-4 py-3 font-body text-navy placeholder:text-navy/40 focus:outline-none focus:border-verm transition-colors duration-300 resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-navy text-paper font-semibold text-lg px-8 py-4 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? "Releasing the line..." : "Send It To The Sky"}
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
